import React, { useState } from "react";
import { Text } from "../custom-components";
import { Text, Button, ActionButton, Icon } from "../custom-components";
import { useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import PII from "./PII";
import DataProcessor from "./DataProcessor";
import ProcessingActivity from "./ProcessingActivity";
import Purpose from "./Purpose";
import "../styles/masterSetup.css";
import CustomToast from "./CustomToastContainer";
import { Slide, ToastContainer, toast } from "react-toastify";
import "../styles/toast.css";
import { languages } from "../utils/languages";
import { IcFilter, IcDownload, IcInfo } from "../custom-components/Icon";
import { BsSearch } from "react-icons/bs";



const MasterSetup = () => {
  const multilingualConfigSaved = useSelector((state) => state.common.multilingualConfigSaved);
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const tabParam = searchParams.get('tab');
  const [showMultilingualWarningModal, setShowMultilingualWarningModal] = useState(false);
  const [activeTabState, setActiveTabState] = useState("PII");
  const [selectedLanguage, setSelectedLanguage] = useState(languages[0].value);
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const [search, setSearch] = useState("");

  React.useEffect(() => {
    if (tabParam) {
      setActiveTabState(tabParam);
    }
  }, [tabParam]);

  const activeTab = tabParam || activeTabState;

  const handleTabChange = (key) => {
    setActiveTabState(key);
    navigate(`/master?tab=${key}`);
  };

  const handleLanguageChange = (e) => {
    const selectedLang = e.target.value;
    
    // Allow English without config check
    if (selectedLang === "en") {
      setSelectedLanguage(selectedLang);
      return;
    }
    
    // Check if multilingual config is saved
    if (!multilingualConfigSaved) {
      setShowMultilingualWarningModal(true);
      // Reset dropdown to previous value
      e.target.value = selectedLanguage || "en";
      return;
    }
    
    setSelectedLanguage(selectedLang);
    setSelectedLanguage(selectedLang);
  };

  const getPageTitle = () => {
    switch (activeTab) {
      case "DataProcessor": return "Data Processors";
      case "ProcessingActivity": return "Processing Activities";
      case "Purpose": return "Processing Purposes";
      default: return "Master Data (PII data types, items)";
    }
  };

  const getPageSubtitle = () => {
    switch (activeTab) {
      case "DataProcessor": return "Configure and manage third-party data processors.";
      case "ProcessingActivity": return "Configure and manage processing activities.";
      case "Purpose": return "Configure and manage data processing purposes.";
      default: return "Configure master data including PII categories, data processors, processing activities and purposes.";
    }
  };

  const languageSelector = (
    <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
      <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '12px' }}>
        <Text appearance="body-s" color="primary-grey-80">Select language to view and edit translation</Text>
        <select
          value={selectedLanguage}
          onChange={handleLanguageChange}
          style={{
            width: '240px',
            padding: '8px',
            borderRadius: '6px',
            border: '1px solid #ccc',
          }}
        >
          {languages.map((lang) => (
            <option key={lang.value} value={lang.value}>
              {lang.label}
            </option>
          ))}
        </select>
      </div>
    </div>
  );

  const renderContent = () => {
    switch (activeTab) {
      case "PII":
        return <PII selectedLanguage={selectedLanguage} languageSelector={languageSelector} pageTitle={getPageTitle()} pageSubtitle={getPageSubtitle()} />;
      case "DataProcessor":
        return <DataProcessor selectedLanguage={selectedLanguage} languageSelector={languageSelector} pageTitle={getPageTitle()} pageSubtitle={getPageSubtitle()} />;
      case "ProcessingActivity":
        return <ProcessingActivity selectedLanguage={selectedLanguage} languageSelector={languageSelector} pageTitle={getPageTitle()} pageSubtitle={getPageSubtitle()} />;
      case "Purpose":
        return <Purpose selectedLanguage={selectedLanguage} languageSelector={languageSelector} pageTitle={getPageTitle()} pageSubtitle={getPageSubtitle()} />;
      default:
        return null;
    }
  };

  return (
    <>
      <div className="configurePage" style={{ marginTop: 0, paddingTop: 0 }}>
        <div className="ms-page">
          <div className="mainDiv-table-and-container">
            {/* Tab Content */}
            <div className="masterSetup-new-css-tab-content">
              {renderContent()}
            </div>
          </div>
        </div>
      </div>
      {/* Multilingual Warning Modal */}
      {showMultilingualWarningModal && (
        <div className="modal-overlay" onClick={() => setShowMultilingualWarningModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600' }}>
                Multilingual Support Required
              </h2>
              <button
                className="modal-close-btn"
                onClick={() => setShowMultilingualWarningModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#666',
                }}
              >
                ×
              </button>
            </div>
            
            <div className="modal-body">
              <Text appearance="body-s" color="primary-grey-80" style={{ marginBottom: '24px' }}>
                You need to configure multilingual support in the consent configuration to enable the translate functionality.
              </Text>

              <div style={{
                display: 'flex',
                gap: '12px',
                justifyContent: 'flex-end',
              }}>
                <Button
                  kind="secondary"
                  size="medium"
                  label="Cancel"
                  onClick={() => setShowMultilingualWarningModal(false)}
                />
                <ActionButton
                  kind="primary"
                  size="medium"
                  label="Go to Configuration"
                  onClick={() => {
                    setShowMultilingualWarningModal(false);
                    navigate("/consent");
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      <ToastContainer
        position="bottom-left"
        autoClose={3000}
        hideProgressBar
        closeOnClick
        pauseOnHover
        draggable
        closeButton={false}
        toastClassName={() => "toast-wrapper"}
        transition={Slide}
      />
    </>
  );
};

export default MasterSetup;
